// src/components/Vibefilter.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

// icons
import GroupsIcon from '@mui/icons-material/Groups';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import CelebrationIcon from '@mui/icons-material/Celebration';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import StarIcon from '@mui/icons-material/Star';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import HighlightIcon from '@mui/icons-material/Highlight';

// Vibes
export const VIBES = [
  'Squad Goals',
  'Dance Floor',
  'Celebration Vibes',
  'Sweet Moments',
  'Too Funny',
  'Glowing Up',
  'Iconic',
  'Caught in Action',
  'Spotlight Moments',
];

// icon mapping
const VIBE_ICONS = {
  'Squad Goals': <GroupsIcon fontSize="small" sx={{ mr: 1 }} />,
  'Dance Floor': <MusicNoteIcon fontSize="small" sx={{ mr: 1 }} />,
  'Celebration Vibes': <CelebrationIcon fontSize="small" sx={{ mr: 1 }} />,
  'Sweet Moments': <FavoriteIcon fontSize="small" sx={{ mr: 1 }} />,
  'Too Funny': <EmojiEmotionsIcon fontSize="small" sx={{ mr: 1 }} />,
  'Glowing Up': <WbSunnyIcon fontSize="small" sx={{ mr: 1 }} />,
  'Iconic': <StarIcon fontSize="small" sx={{ mr: 1 }} />,
  'Caught in Action': <SportsKabaddiIcon fontSize="small" sx={{ mr: 1 }} />,
  'Spotlight Moments': <HighlightIcon fontSize="small" sx={{ mr: 1 }} />,
};

/* ====================== VibeSelector ====================== */
export function VibeSelector({
  selectedVibe,
  onVibeChange,
  uploaderName,
  onNameChange,
  inputBg = '#fff',
  inputColor = '#000',
}) {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const onEventPage = /(\/event|\/events)(\/|$)/i.test(path);
  const isLocked = !onEventPage;

  const [dialogOpen, setDialogOpen] = useState(false);
  const openDialog = () => setDialogOpen(true);

  useEffect(() => {
    if (!dialogOpen) return;
    const t = setTimeout(() => setDialogOpen(false), 3000);
    return () => clearTimeout(t);
  }, [dialogOpen]);

  const stopIfLocked = (e) => {
    if (!isLocked) return;
    e.preventDefault();
    e.stopPropagation();
    openDialog();
  };

  return (
    <>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          alignItems: 'center',
          width: '100%',
        }}
      >
        {/* Name */}
        <Box sx={{ position: 'relative', width: '100%', maxWidth: 420 }}>
          <TextField
            fullWidth
            label="Add your name"
            size="small"
            value={uploaderName}
            onChange={(e) => !isLocked && onNameChange?.(e.target.value)}
            onClick={stopIfLocked}
            onKeyDown={stopIfLocked}
            inputProps={{ readOnly: isLocked, tabIndex: isLocked ? -1 : 0 }}
            sx={{
              backgroundColor: inputBg,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': { borderColor: inputColor },
                '&:hover fieldset': { borderColor: inputColor },
                '&.Mui-focused fieldset': { borderColor: inputColor },
                color: inputColor,
              },
              '& .MuiInputLabel-root': { color: inputColor },
            }}
          />
        </Box>

        {/* Vibe chips */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 420,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: 'clamp(220px, 74vw, 250px)',
              display: 'flex',
              gap: 1,
              overflowX: 'auto',
              flexWrap: 'nowrap',
              py: 0.5,
              px: 0.25,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
            onMouseDown={stopIfLocked}
            onClick={stopIfLocked}
            onKeyDown={stopIfLocked}
          >
            {VIBES.map((v) => {
              const active = selectedVibe === v;

              return (
                <Button
                  key={v}
                  type="button"
                  onClick={() => !isLocked && onVibeChange?.(active ? '' : v)}
                  startIcon={VIBE_ICONS[v]}
                  sx={{
                    flex: '0 0 auto',
                    borderRadius: 999,
                    px: 1.5,
                    py: 0.85,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    minHeight: 40,
                    backgroundColor: active ? '#111' : 'rgba(0,0,0,0.06)',
                    color: active ? '#fff' : '#111',
                    border: active
                      ? '1px solid #111'
                      : '1px solid rgba(0,0,0,0.10)',
                    boxShadow: active
                      ? '0 2px 8px rgba(0,0,0,0.14)'
                      : 'none',
                    '&:hover': {
                      backgroundColor: active
                        ? '#111'
                        : 'rgba(0,0,0,0.10)',
                    },
                    '& .MuiButton-startIcon': {
                      mr: 0.75,
                      '& svg': {
                        fontSize: 18,
                        color: active ? '#fff' : '#111',
                      },
                    },
                  }}
                >
                  {v}
                </Button>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* short auto-close modal */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        keepMounted
      >
        <DialogTitle>Heads up</DialogTitle>
        <DialogContent>
          This will be live on the event page.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* ====================== VibeFilterBar ====================== */
export function VibeFilterBar({
  media,
  filterVibe,
  onFilterChange,
  tabBg = '#1976d2',
  tabColor = '#ffffff',
  disabled = false,
  showCounts = true,
  mediaForCounts,
  overrideAllCount,
}) {
  const source = mediaForCounts ?? media;

  const isHttpish = (u) =>
    typeof u === 'string' && /^https?:\/\//i.test(u);

  const validPhoto = (m) =>
    m?.type === 'photo' &&
    (isHttpish(m?.url) || isHttpish(m?.thumbnailUrl));

  const norm = (s) => (s || '').trim().toLowerCase();

  const counts = VIBES.reduce((acc, vibe) => {
    const nv = norm(vibe);

    acc[vibe] = source.filter(
      (m) => validPhoto(m) && norm(m?.vibe) === nv
    ).length;

    return acc;
  }, {});

  const allLoaded = source.filter(validPhoto).length;

  counts.all = Number.isFinite(overrideAllCount)
    ? overrideAllCount
    : allLoaded;

  // Keep labels clean. No guest-facing counts.
  const label = (v) => v;

  const visibleVibes = VIBES.filter(
    (v) => (counts[v] ?? 0) > 0
  );

  useEffect(() => {
    if (disabled) return;

    if (
      filterVibe &&
      filterVibe !== 'all' &&
      !visibleVibes.includes(filterVibe)
    ) {
      onFilterChange('all');
    }
  }, [disabled, filterVibe, visibleVibes, onFilterChange]);

  const containerSx = {
    width: '100%',
    backgroundColor: '#ffffff',
    borderTop: '1px solid rgba(0,0,0,0.06)',
    px: 0,
    py: 0.75,
    position: 'relative',
  };

  const railSx = {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 1,
    overflowX: 'auto',
    overflowY: 'hidden',
    alignItems: 'center',
    WebkitOverflowScrolling: 'touch',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    px: 2,
    py: 0.25,
    scrollPaddingLeft: '16px',
    scrollPaddingRight: '16px',
  };

  const chipSx = (active) => ({
    flex: '0 0 auto',
    borderRadius: 999,
    px: 1.75,
    py: 0.7,
    minHeight: 38,
    fontWeight: 700,
    fontSize: 'clamp(12px, 1.6vw, 14px)',
    letterSpacing: 0,
    whiteSpace: 'nowrap',
    color: '#111',
    backgroundColor: active
      ? 'rgba(0,0,0,0.08)'
      : '#ffffff',
    border: '1px solid rgba(0,0,0,0.12)',
    textTransform: 'none',
    boxShadow: 'none',
    transition: 'background-color .18s ease',
    '&:hover': {
      backgroundColor: active
        ? 'rgba(0,0,0,0.08)'
        : 'rgba(0,0,0,0.04)',
    },
    '& .MuiButton-startIcon': {
      mr: 0.75,
      '& svg': {
        fontSize: 18,
        color: '#111',
      },
    },
  });

  return (
    <Box sx={containerSx}>
      <Box sx={railSx}>
        <Button
          onClick={() => !disabled && onFilterChange('all')}
          disabled={disabled}
          sx={chipSx(filterVibe === 'all')}
        >
          All
        </Button>

        {visibleVibes.map((v) => (
          <Button
            key={v}
            startIcon={VIBE_ICONS[v]}
            onClick={() => !disabled && onFilterChange(v)}
            disabled={disabled}
            sx={chipSx(filterVibe === v)}
          >
            {label(v)}
          </Button>
        ))}
      </Box>
    </Box>
  );
}